import { useRef, useState } from "react";
import { ImagePlus, Trash2, X, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export const PhotosView = ({ est, reload }) => {
  const { t, lang } = useLanguage();
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const gallery = est.gallery || [];
  const mainPhoto = est.image;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/pro/establishment/photos", fd);
        ok += 1;
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
    if (ok) toast.success(ok > 1 ? `${ok} photos ajoutées à ta galerie` : "Photo ajoutée à ta galerie");
    setBusy(false);
    reload();
  };

  const remove = async (url) => {
    if (!window.confirm(t("Retirer cette photo de la galerie ?"))) return;
    try {
      await api.delete("/pro/establishment/photos", { data: { url } });
      toast.success(t("Photo retirée"));
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveOrder = async (next, setMain = false, successMsg = "Ordre des photos enregistré") => {
    try {
      await api.patch("/pro/establishment/photos", { gallery: next, set_main: setMain });
      toast.success(successMsg);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const move = (i, dir) => {
    const next = [...gallery];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    saveOrder(next);
  };

  const setAsMain = (url) => {
    const next = [url, ...gallery.filter((p) => p !== url)];
    saveOrder(next, true, "Photo principale mise à jour");
  };

  return (
    <div data-testid="pro-photos-view" className="mt-6 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
      <h2 className="text-base font-bold text-slate-900">{t("Galerie photos")}</h2>
      <p className="mt-1 text-xs text-slate-500">
        {t("Ces photos apparaissent sur ta page publique GoSpot. La première photo est ta photo principale (visible dans les listes).")}
      </p>
      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {gallery.map((p, i) => {
          const isMain = p === mainPhoto || (i === 0 && !mainPhoto);
          return (
            <div key={p + i} data-testid={`photos-item-${i}`} className={`group relative overflow-hidden rounded-2xl ${isMain ? "ring-2 ring-[#2563EB]" : ""}`}>
              <button type="button" onClick={() => setLightbox(src(p))} className="block w-full">
                <img src={src(p)} alt={`${est.name} — photo ${i + 1}`} className="h-36 w-full object-cover transition duration-500 group-hover:scale-105" />
              </button>
              {isMain && (
                <span data-testid={`photos-main-badge-${i}`} className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-[#2563EB] px-2 py-0.5 text-[9px] font-bold text-white">
                  <Star className="h-2.5 w-2.5 fill-white" />
                  Principale
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-slate-900/70 to-transparent p-2 opacity-0 transition group-hover:opacity-100">
                <div className="flex gap-1">
                  <button type="button" data-testid={`photos-move-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label={t("Déplacer à gauche")} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 transition hover:text-[#2563EB] disabled:opacity-40">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button type="button" data-testid={`photos-move-right-${i}`} onClick={() => move(i, 1)} disabled={i === gallery.length - 1} aria-label={t("Déplacer à droite")} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 transition hover:text-[#2563EB] disabled:opacity-40">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-1">
                  {!isMain && (
                    <button type="button" data-testid={`photos-set-main-${i}`} onClick={() => setAsMain(p)} aria-label={t("Définir comme photo principale")} title={t("Définir comme photo principale")} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 transition hover:text-amber-500">
                      <Star className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" data-testid={`photos-delete-${i}`} onClick={() => remove(p)} aria-label={t("Retirer cette photo")} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-slate-600 transition hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          data-testid="photos-upload-tile"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          className="flex h-36 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-60"
        >
          <ImagePlus className="h-6 w-6" />
          <span className="text-xs font-semibold">{busy ? "Envoi…" : "Ajouter des photos"}</span>
        </button>
        <input ref={fileInput} type="file" accept="image/*" multiple className="hidden" data-testid="photos-file-input" onChange={upload} />
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm" onClick={() => setLightbox(null)} data-testid="photos-lightbox">
          <img src={lightbox} alt={t("Photo agrandie")} className="max-h-[85vh] max-w-full rounded-3xl object-contain shadow-2xl" />
          <button type="button" aria-label={t("Fermer")} className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};
